import { parlorRegion } from "@/constants/parlorRegion";
import {
  BTypeCardView,
  CTypeBreadCrumb,
  FullWidthRadiusButton,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildCrematoriumPageSchema,
  CrematoriumRegionFAQ,
  GOI_WEB_URL,
  RichSearch,
  // HLine,
  SidoName,
} from "goi_common";

import {
  Box,
  // Button, HStack,
  Stack,
  VStack,
} from "@chakra-ui/react";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";
import { GetStaticPaths, GetStaticProps } from "next";

type CrematoriumDetail = (typeof crematoriums)[number];

import Head from "next/head";

const { targetSidos, regionColor } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO.entries()).filter(([code]) =>
    targetSidos.includes(code.toString())
  );

  const paths = sidoList
    .map((sido) => {
      const sigungus = FUNERAL_SIGUNGU.get(sido[0]);

      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              sido: sido[1],
              sigungu,
            },
          };
        });
      }

      return [];
    })
    .filter((i) => i != null);

  return {
    paths: paths.flat(),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { sido, sigungu } = params as { sido: string; sigungu: string };

  const sidoCode = Array.from(FUNERAL_SIDO.entries()).find(
    ([, value]) => value === SidoName[sido as keyof typeof SidoName]
  )?.[0];

  const sigunguCode = Array.from(FUNERAL_SIGUNGU.values())
    .flatMap((m) => Array.from(m.entries()))
    .find(([, name]) => name === sigungu)?.[0];

  /** 시군구까지 일치하는 것을 앞으로 배치, 시도일치를 뒤로 배치 */
  const regionCrematoriums = crematoriums
    .filter((c) => targetSidos.includes(c.sido.toString()))
    .sort((a, b) => {
      // 시도 일치 여부 비교 (1순위)
      const sidoCompare =
        Number(b.sido === Number(sidoCode)) -
        Number(a.sido === Number(sidoCode));

      if (sidoCompare !== 0) return sidoCompare;

      // 시군구 일치 여부 비교 (2순위)
      return (
        Number(b.sigungu === Number(sigunguCode)) -
        Number(a.sigungu === Number(sigunguCode))
      );
    });

  const district =
    sigungu.length >= 3 && (sigungu.endsWith("시") || sigungu.endsWith("구"))
      ? sigungu.slice(0, -1)
      : sigungu;

  const regionText = `${sido} ${district}`;

  const firstCrematorium = regionCrematoriums?.[0];
  const secondCrematorium = regionCrematoriums?.[1];

  const title = `(${regionText}) 지역 화장장, 화장터 시설, 비용, 추천, 정보 예약 안내 :: 강원장례정보공개 (고이장례연구소)`;
  const description = `${regionText} 화장장(화장터, 승화원) 시설, 비용, 추천, 정보 예약 정보 종합 안내 ${
    firstCrematorium ? `${firstCrematorium.companyname},` : ""
  } ${
    secondCrematorium ? `${secondCrematorium.companyname}` : ""
  } 등 ${regionText} 화장장, 화장터 위치, 이용료, 예약방법 및 장례 서비스 정보 (고이장례연구소)`;

  const keywords = `${regionText} 화장장, ${sido} 화장장, ${sigungu} 화장장, ${district} 화장장, 
  ${regionText} 화장터, ${sido} 화장터, ${sigungu} 화장터, ${district} 화장터, 
  ${regionText} 화장장 추천, ${sido} 화장장 추천, ${sigungu} 화장장 추천, ${district} 화장장 추천, 
  ${regionText} 화장장 정보, ${sido} 화장장 정보, ${sigungu} 화장장 정보, ${district} 화장장 정보, 
  ${regionText} 화장장 비교, ${sido} 화장장 비교, ${sigungu} 화장장 비교, ${district} 화장장 비교, 
  ${regionText} 화장장 예약, ${sido} 화장장 예약, ${sigungu} 화장장 예약, ${district} 화장장 예약,
  ${regionText} 화장장 비용, ${sido} 화장장 비용, ${sigungu} 화장장 비용, ${district} 화장장 비용,
  ${regionText} 화장터 추천, ${sido} 화장터 추천, ${sigungu} 화장터 추천, ${district} 화장터 추천, 
  ${regionText} 화장터 정보, ${sido} 화장터 정보, ${sigungu} 화장터 정보, ${district} 화장터 정보, 
  ${regionText} 화장터 비교, ${sido} 화장터 비교, ${sigungu} 화장터 비교, ${district} 화장터 비교, 
  ${regionText} 화장터 예약, ${sido} 화장터 예약, ${sigungu} 화장터 예약, ${district} 화장터 예약,
  ${regionText} 화장터 비용, ${sido} 화장터 비용, ${sigungu} 화장터 비용, ${district} 화장터 비용,
  ${sido} 화장예약, ${sido} 화장 비용, 
  ${sigungu} 화장예약, ${sigungu} 화장 비용,
  ${regionText} 화장예약, ${regionText} 화장 비용,
  ${district} 화장예약, ${district} 화장 비용,
  ${regionCrematoriums
    ?.map((c) => {
      const name =
        c.companyname.includes("화장장") || c.companyname.includes("화장터")
          ? c.companyname
          : `${c.companyname} 화장장`;
      return `${name}, ${name} 화장비용, ${name} 화장예약, ${name} 서류, ${name} 시간, ${name} 시설, ${name} 유택동산, ${name} 근처 화장장, ${name} 근처 화장터`;
    })
    .join(", ")}, 화장장, 화장터, 화장예약, 화장 비용, 화장지원금`;

  return {
    props: {
      sidoName: sido,
      sigunguName: sigungu,
      district,
      regionCrematoriums,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage: regionCrematoriums[0]?.images[0]?.url,
    },
  };
};

function CrematoriumRegionSigunguPage({
  sidoName,
  sigunguName,
  regionCrematoriums,
  district,
  pageOgTitle,
}: {
  sidoName: string;
  sigunguName: string;
  regionCrematoriums: CrematoriumDetail[];
  district: string;
  pageOgTitle: string;
}) {
  const defaultCrematorium = regionCrematoriums[0];
  const totalCrematoriums = regionCrematoriums.length;

  const regionText = `${sidoName} ${district}`;

  const crematoriumPageSchema = buildCrematoriumPageSchema(regionCrematoriums, {
    regionText,
    goiWebUrl: GOI_WEB_URL,
  });

  const CrematoriumRegionHeroSectionB = ({ title }: { title: string }) => {
    return (
      <VStack width="100%" height="200px" position="relative">
        <img
          src={
            defaultCrematorium?.images[0]?.url
              ? defaultCrematorium?.images[0]?.url
              : ""
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          alt={`${defaultCrematorium?.companyname} 시설 사진`}
        />
        <Stack
          width="100%"
          overflow="hidden"
          position="absolute"
          height="200px"
          zIndex={2}
          style={{
            background:
              "linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%)",
          }}
          padding="60px 20px"
        >
          <p
            className="head_24_b white"
            style={{
              whiteSpace: "pre-wrap",
            }}
          >
            {title}
          </p>
        </Stack>
      </VStack>
    );
  };

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={crematoriumPageSchema} />

      <main>
        <CTypeBreadCrumb
          firstItem={{
            text: `화장 비용`,
            href: GOI_WEB_URL + "/match/request/?utm_source=dogsound",
            event: {
              domain: "gangwon",
              path: "crematorium",
              trigger_and_target: "br_crumb_mth_req",
            },
          }}
          secondItem={{
            text: `${sidoName} 화장장`,
            href: ``,
            event: {
              domain: "gangwon",
              path: "crematorium",
              trigger_and_target: "br_crumb_sido_prlr_list",
            },
          }}
          thirdItem={{
            text: `${district} 화장장`,
            href: ``,
            event: {
              domain: "gangwon",
              path: "crematorium",
              trigger_and_target: "br_crumb_sigungu_prlr_list",
            },
          }}
          goiHomeEvent={{
            domain: "gangwon",
            path: "crematorium",
            trigger_and_target: "br_crumb_goi_home",
          }}
        />

        <section
          style={{
            margin: "0px 12px",
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
          }}
        >
          <Box margin="0px 0px 20px 0px">
            <CrematoriumRegionHeroSectionB
              title={`${regionText} \n화장 비용 및 예약 정보`}
            />
          </Box>
        </section>

        <section
          style={{
            background: "white",
            margin: "20px 12px",
            padding: "40px 0px 20px",
            boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.12)",
          }}
        >
          <VStack padding="10px 20px">
            <VStack textAlign="center" gap="10px">
              <p className="subtitle_16_sb gray900">
                {regionText} 화장 비용 및 예약 정보
              </p>
              <p className="body_14_m gray800">
                주소지가 {regionText} 및 인근 지역인 분이시라면
                <br />
                관내/관외 적용 여부에 따른 비용을 확인하시는 것이 필요합니다.
              </p>
            </VStack>

            <Stack gap="10px" marginTop="30px">
              <FullWidthRadiusButton
                backgroundColor={regionColor.c400}
                text="화장 비용 문의 및 예약"
                href={"tel:1666-9784"}
                event={{
                  domain: "gangwon",
                  path: "crematorium",
                  trigger_and_target: "goi_call",
                }}
              />
            </Stack>
          </VStack>
        </section>

        <section style={{ padding: "20px 16px", marginTop: "20px" }}>
          <VStack
            width="100%"
            gap="5px"
            alignItems="flex-start"
            padding="0px 0px 10px 0px"
          >
            <h2 className="subtitle_16_b gray900">
              {regionText} 지역의 화장장 추천
            </h2>
            <p className="body_14_m gray700">
              {regionText} 지역 및 인근 지역의 화장장을 거리, 비용적인 측면에서
              추천드립니다.
            </p>
          </VStack>

          <VStack as="ul" width="100%" gap="16px">
            {regionCrematoriums.map((crematorium) => (
              <li key={crematorium?.id} style={{ width: "100%" }}>
                <BTypeCardView
                  descriptions={[crematorium?.address]}
                  arrowText="자세히 알아보기"
                  href={`${GOI_WEB_URL}/crematorium-recommendation/${crematorium?.id}/?utm_source=dogsound`}
                  imgSrc={
                    crematorium.images[0] ? crematorium.images[0].url : ""
                  }
                  name={
                    crematorium.companyname.includes("화장장") ||
                    crematorium.companyname.includes("화장터")
                      ? crematorium.companyname
                      : crematorium.companyname + " 화장장"
                  }
                  mainColor={regionColor}
                  hasOneDayBasis={false}
                  event={{
                    domain: "gangwon",
                    path: "crematorium",
                    trigger_and_target: "item_click",
                  }}
                />
              </li>
            ))}
          </VStack>
        </section>

        <CrematoriumRegionFAQ />
      </main>
    </>
  );
}

export default CrematoriumRegionSigunguPage;
